// src/utils/publicClubPaths.ts
//
// Which pages under /:clubid/ a signed-out visitor may open. App.tsx sends
// anyone without a staff or member sign-in to the login page unless the path
// is listed here.
//
// Until 2026-10-09 the list sat inline in App.tsx and had fallen behind the
// public routes: the clinic list, facility booking, the program finder, the
// feedback, shared-media, design and broadcast links, membership invites and
// the athlete login all bounced visitors to the login page, although each is
// a public route with a public API behind it. A new public route must be
// added here too; `publicClubPaths.test.ts` checks every public <Route> in
// App.tsx against this list.

/** First path segment after the club id that is public in full. */
export const PUBLIC_CLUB_SECTIONS: readonly string[] = [
  "",
  "login",
  "register",
  "programs",
  "forgot-password",
  "reset-password",
  "magic-login",
  "about",
  "contact",
  "faq",
  "privacy",
  "privacy-policy",
  "terms",
  "terms-of-service",
  "cookie-policy",
  "eula",
  "support",
  "accessibility-policy",
  "members-summary",
  "kiosk",
  "trial",
  "posts",
  "events",
  "camps",
  "clinics",
  "book",
  "find",
  "embed",
  "sign-waiver",
  "feedback",
  "d",
  "shared",
  "m",
  "accept-invite",
  // Athletes sign in with their own code and keep their own token; the staff
  // and member sign-in check must not send them to the family login.
  "athlete",
];

/** Public pages inside a section that is otherwise signed-in only. */
export const PUBLIC_CLUB_PAGES: readonly string[] = ["profile/support-pin/reset"];

/** True when a signed-out visitor may stay on this /:clubid/... path. */
export function isPublicClubPath(pathname: string): boolean {
  const parts = (pathname || "").split("?")[0].split("/");
  // parts[0] is "" (leading slash), parts[1] is the club id.
  const section = parts[2] || "";
  if (PUBLIC_CLUB_SECTIONS.includes(section)) return true;
  const rest = parts.slice(2).filter(Boolean).join("/");
  return PUBLIC_CLUB_PAGES.some((page) => rest === page || rest.startsWith(`${page}/`));
}
